import Router from 'next/router';

import axios from 'axios';

import { getBaseURL } from './fetch';
import { refreshToken } from './controllers/auth';
import { useCookies } from '../hooks/use-cookies';
import { cookies } from '../constants/cookies';
import { BUSINESS_BLOCKED_CODE } from '../utils/business-blocked';

function getGoogleAnalyticsData() {
  let gaClientId = null;
  if ((window as any)?.gaGlobal?.vid) {
    gaClientId = (window as any)?.gaGlobal?.vid;
  }

  const pattern = /_ga_YL10D93TYS=GS\d\.\d\.(.+?)(?:;|$)/;
  const match = document.cookie.match(pattern);
  const parts = match?.[1].split('.');

  if (!parts) {
    return;
  }

  return {
    ga_client_id: gaClientId,
    ga_session_id: parts.shift(),
    ga_session_number: parts.shift(),
  };
}

const customAxios = axios.create({
  baseURL: getBaseURL(),
});

let refreshPromise: Promise<string> | null = null;
let isHandlingUnauthorized = false;

const decodeTokenPayload = (token?: string): Record<string, any> => {
  try {
    if (!token) return {};

    const [, payload] = token.split('.');
    if (!payload) return {};

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );

    return JSON.parse(window.atob(paddedPayload));
  } catch (error) {
    return {};
  }
};

const getPathBusinessSlug = (path: string): string | null => {
  const normalizedPath = path.split('?')[0].replace(/\/+$/, '');
  const segments = normalizedPath.split('/').filter(Boolean);

  if (segments.length !== 1) return null;

  const [slug] = segments;
  if (!slug || slug === 'admin') {
    return null;
  }

  return slug;
};

const getUnauthorizedRedirectPath = (): string => {
  const currentPath = window.location.pathname;

  if (currentPath === '/admin' || currentPath.startsWith('/admin/')) {
    return '/admin';
  }

  const { getItem } = useCookies();
  const refreshToken = getItem(cookies.REFRESH_TOKEN);
  const tokenPayload = decodeTokenPayload(refreshToken);
  const rememberedBusinessSlug = tokenPayload.businessSlug || null;
  const pathBusinessSlug = getPathBusinessSlug(currentPath);

  return rememberedBusinessSlug
    ? `/${rememberedBusinessSlug}`
    : pathBusinessSlug
    ? `/${pathBusinessSlug}`
    : '/';
};

customAxios.interceptors.request.use((config) => {
  // 🔄 Lê dinamicamente dos cookies a cada request
  const { getItem } = useCookies();
  const token = getItem(cookies.AUTH_TOKEN);

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  const gaData = getGoogleAnalyticsData();

  if (gaData) {
    config.headers['ga-client-id'] = gaData.ga_client_id;
    config.headers['ga-session-id'] = gaData.ga_session_id;
    config.headers['ga-session-number'] = gaData.ga_session_number;
  }

  return config;
});

customAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const failedReqUrl: string = error?.config?.url || '';
    const failedReqStatus: number | undefined = error?.response?.status;
    const failedReqCode: string | undefined = error?.response?.data?.code;

    if (!failedReqStatus) {
      return Promise.reject(error);
    }

    // Negocio bloqueado no meio da sessao: encerra a sessao e volta ao login.
    // O login exibira o toast amigavel de bloqueio (mesmo code).
    if (
      failedReqStatus === 403 &&
      failedReqCode === BUSINESS_BLOCKED_CODE &&
      !failedReqUrl.includes('login')
    ) {
      handleUnauthorizedLogout();
      return Promise.reject(error.response);
    }

    if (
      failedReqStatus === 401 &&
      !originalRequest?._retry &&
      !failedReqUrl.includes('refresh') &&
      !failedReqUrl.includes('login')
    ) {
      try {
        const accessToken = await handleRefreshToken();

        originalRequest._retry = true;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;

        return customAxios(originalRequest);
      } catch (refreshError: any) {
        handleUnauthorizedLogout();
        return Promise.reject(refreshError?.response || refreshError);
      }
    }

    if (failedReqStatus === 401) {
      handleUnauthorizedLogout();
    }

    return Promise.reject(error.response);
  }
);

async function handleRefreshToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const { getItem, setItem } = useCookies();
      const token = getItem(cookies.REFRESH_TOKEN);

      if (!token) {
        throw new Error('Missing refresh token');
      }

      const { accessToken, refreshToken: newRefreshToken } = await refreshToken(
        token
      );
      setItem(cookies.AUTH_TOKEN, accessToken);
      setItem(cookies.REFRESH_TOKEN, newRefreshToken);
      setAuthToken(accessToken);

      return accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

function handleUnauthorizedLogout(): void {
  if (isHandlingUnauthorized) return;

  isHandlingUnauthorized = true;
  const redirectPath = getUnauthorizedRedirectPath();

  const { removeItem } = useCookies();
  removeItem(cookies.AUTH_TOKEN);
  removeItem(cookies.REFRESH_TOKEN);
  setAuthToken('');

  Router.replace(redirectPath).finally(() => {
    isHandlingUnauthorized = false;
  });
}

export const setAuthToken = (token: string): void => {
  if (token) {
    customAxios.defaults.headers['Authorization'] = `Bearer ${token}`;
    return;
  }

  delete customAxios.defaults.headers['Authorization'];
};

export default customAxios;
